import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { MotionBox, fadeInUp, useMotionProps } from '../motion';

/**
 * Consistent section header: eyebrow pill, gradient headline and subtitle.
 * `onDark` switches to the light-on-dark treatment used by the ERP and Facts bands.
 */
const SectionHeading = ({ id, eyebrow, title, subtitle, onDark = false }) => {
  const theme = useTheme();
  const motionProps = useMotionProps();

  return (
    <MotionBox {...motionProps(fadeInUp())} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
      {eyebrow && (
        <Box
          sx={{
            display: 'inline-block',
            mb: 2.5,
            px: 2,
            py: 0.75,
            borderRadius: 99,
            backgroundColor: onDark ? alpha('#fff', 0.14) : alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${onDark ? alpha('#fff', 0.28) : alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: onDark ? 'blur(10px)' : undefined
          }}
        >
          <Typography
            variant="overline"
            component="span"
            sx={{ fontSize: '0.72rem', color: onDark ? '#fff' : 'primary.main', lineHeight: 2 }}
          >
            {eyebrow}
          </Typography>
        </Box>
      )}

      <Typography
        id={id}
        variant="h2"
        component="h2"
        sx={{
          fontWeight: 800,
          mb: 2.5,
          ...(onDark
            ? { color: '#fff' }
            : {
                background: `linear-gradient(120deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent'
              })
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="h6"
          component="p"
          sx={{
            maxWidth: 820,
            mx: 'auto',
            lineHeight: 1.7,
            fontWeight: 400,
            color: onDark ? alpha('#fff', 0.9) : 'text.secondary'
          }}
        >
          {subtitle}
        </Typography>
      )}
    </MotionBox>
  );
};

export default SectionHeading;
